import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Delete, Lock } from 'lucide-react';

export const PinLogin = () => {
  const { login } = useAuth();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = useCallback(async (pinToUse) => {
    if (pinToUse.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    const result = await login(pinToUse);
    
    if (result.success) {
      toast.success(`Welcome, ${result.user.name}!`);
    } else {
      setError(result.error);
      setPin('');
      toast.error(result.error);
    }
    
    setLoading(false);
  }, [login]);

  const handleNumberClick = useCallback((num) => {
    if (loading) return;
    setPin(prev => {
      if (prev.length < 4) {
        const newPin = prev + num;
        setError('');
        if (newPin.length === 4) {
          setTimeout(() => handleLogin(newPin), 50);
        }
        return newPin;
      }
      return prev;
    });
  }, [loading, handleLogin]);

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleClear = () => {
    setPin('');
    setError('');
  };

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (loading) return;
      if (e.key >= '0' && e.key <= '9') {
        handleNumberClick(e.key);
      } else if (e.key === 'Backspace') {
        handleDelete();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, handleNumberClick]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm bg-white rounded-3xl shadow-xl">
        <CardHeader className="text-center pb-2">
          <img src="/tappy-logo.png" alt="Tappy" className="h-12 mx-auto mb-4" />
          <CardTitle className="font-serif text-2xl text-stone-800">Staff Login</CardTitle>
          <p className="text-sm text-stone-500 mt-1">Enter your 4-digit PIN</p>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          {/* PIN Display */}
          <div className="flex justify-center gap-3 mb-6">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                  pin.length > i
                    ? 'border-[#D97757] bg-[#D97757]/10 text-[#D97757]'
                    : 'border-stone-200 bg-stone-50'
                }`}
              >
                {pin.length > i ? '•' : ''}
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-center text-red-500 text-sm mb-4 bg-red-50 py-2 rounded-lg">
              {error}
            </div>
          )}

          {/* Number Pad */}
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <Button
                key={num}
                variant="outline"
                className="h-14 text-xl font-semibold rounded-xl hover:bg-stone-100 active:scale-95 transition-all"
                onClick={() => handleNumberClick(num.toString())}
                data-testid={`pin-key-${num}`}
                disabled={loading}
              >
                {num}
              </Button>
            ))}
            <Button
              variant="outline"
              className="h-14 text-sm font-medium rounded-xl hover:bg-red-50 hover:text-red-600 hover:border-red-200"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              className="h-14 text-xl font-semibold rounded-xl hover:bg-stone-100 active:scale-95 transition-all"
              onClick={() => handleNumberClick('0')}
              disabled={loading}
            >
              0
            </Button>
            <Button
              variant="outline"
              className="h-14 rounded-xl hover:bg-stone-100"
              onClick={handleDelete}
              disabled={loading}
            >
              <Delete className="w-5 h-5" />
            </Button>
          </div>

          {/* Loading indicator */}
          {loading && (
            <div className="flex items-center justify-center mt-4 text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span>Signing in...</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PinLogin;
